import type { AppendedPointInfo } from '@service/liveData';
import type { AppStore } from '@state/store';
import type { Disposable } from '@type/disposable';
import type { Maidr, NavigateCallback } from '@type/grammar';
import type { Observer } from '@type/observable';
import type { TraceState } from '@type/state';
import { Context } from '@model/context';
import { Figure } from '@model/plot';
import { AudioService } from '@service/audio';
import { AutoplayService } from '@service/autoplay';
import { BrailleService } from '@service/braille';
import { CandlestickDeltaService } from '@service/candlestickDelta';
import { ChatService } from '@service/chat';
import { CommandExecutor } from '@service/commandExecutor';
import { CommandPaletteService } from '@service/commandPalette';
import { DescriptionService } from '@service/description';
import { DisplayService } from '@service/display';
import { FormatterService } from '@service/formatter';
import { GoToExtremaService } from '@service/goToExtrema';
import { HelpService } from '@service/help';
import { HighContrastService } from '@service/highContrast';
import { HighlightService } from '@service/highlight';
import { KeybindingService, Mousebindingservice } from '@service/keybinding';
import { isAppendedPointFocused } from '@service/liveData';
import { MonitorService } from '@service/monitor';
import { NotificationService } from '@service/notification';
import { ReviewService } from '@service/review';
import { RotorNavigationService } from '@service/rotor';
import { SettingsService } from '@service/settings';
import { LocalStorageService } from '@service/storage';
import { TextService } from '@service/text';
import { BrailleViewModel } from '@state/viewModel/brailleViewModel';
import { CandlestickDeltaViewModel } from '@state/viewModel/candlestickDeltaViewModel';
import { ChatViewModel } from '@state/viewModel/chatViewModel';
import { CommandPaletteViewModel } from '@state/viewModel/commandPaletteViewModel';
import { DescriptionViewModel } from '@state/viewModel/descriptionViewModel';
import { DisplayViewModel } from '@state/viewModel/displayViewModel';
import { GoToExtremaViewModel } from '@state/viewModel/goToExtremaViewModel';
import { HelpViewModel } from '@state/viewModel/helpViewModel';
import { ViewModelRegistry } from '@state/viewModel/registry';
import { ReviewViewModel } from '@state/viewModel/reviewViewModel';
import { RotorNavigationViewModel } from '@state/viewModel/rotorNavigationViewModel';
import { SettingsViewModel } from '@state/viewModel/settingsViewModel';
import { TextViewModel } from '@state/viewModel/textViewModel';
import { resolveSubplotLayout } from '@util/subplotLayout';

/**
 * Main controller class that orchestrates all services, view models, and interactions for the MAIDR application.
 */
export class Controller implements Disposable {
  // Mutable: replaced in place on live data updates (see updateData).
  private figure: Figure;
  private readonly context: Context;

  private readonly displayService: DisplayService;
  private readonly notificationService: NotificationService;
  private readonly settingsService: SettingsService;
  private readonly formatterService: FormatterService;

  private readonly audioService: AudioService;
  private readonly brailleService: BrailleService;
  private readonly candlestickDeltaService: CandlestickDeltaService;
  private readonly goToExtremaService: GoToExtremaService;
  private readonly textService: TextService;
  private readonly reviewService: ReviewService;
  private readonly rotorNavigationService: RotorNavigationService;

  private readonly autoplayService: AutoplayService;
  private readonly highContrastService: HighContrastService;
  private readonly monitorService: MonitorService;
  private readonly highlightService: HighlightService;
  private readonly descriptionService: DescriptionService;
  private readonly helpService: HelpService;
  private readonly chatService: ChatService;

  private readonly textViewModel: TextViewModel;
  private readonly brailleViewModel: BrailleViewModel;
  private readonly candlestickDeltaViewModel: CandlestickDeltaViewModel;
  private readonly goToExtremaViewModel: GoToExtremaViewModel;
  private readonly reviewViewModel: ReviewViewModel;
  private readonly descriptionViewModel: DescriptionViewModel;
  private readonly displayViewModel: DisplayViewModel;
  private readonly helpViewModel: HelpViewModel;
  private readonly chatViewModel: ChatViewModel;
  private readonly settingsViewModel: SettingsViewModel;
  private readonly rotorNavigationViewModel: RotorNavigationViewModel;
  private readonly commandPaletteViewModel: CommandPaletteViewModel;

  private readonly keybinding: KeybindingService;
  private readonly mousebinding: Mousebindingservice;
  private readonly commandExecutor: CommandExecutor;
  private readonly viewModelRegistry: ViewModelRegistry;

  /**
   * Initializes the controller with all necessary services, view models, and bindings.
   * @param maidr - The MAIDR configuration object containing plot data and settings
   * @param plot - The HTML element containing the plot to be made accessible
   * @param store - The Redux store instance for this plot's state management
   */
  public constructor(maidr: Maidr, plot: HTMLElement, store: AppStore) {
    this.viewModelRegistry = new ViewModelRegistry();
    this.figure = new Figure(maidr);
    this.figure.applyLayout(resolveSubplotLayout(this.figure.subplots));
    this.context = new Context(this.figure);

    this.notificationService = new NotificationService();
    this.formatterService = new FormatterService(maidr);
    this.textService = new TextService(this.notificationService, this.formatterService);
    this.displayService = new DisplayService(this.context, plot, this.textService);
    this.settingsService = new SettingsService(
      new LocalStorageService(),
      this.displayService,
    );
    this.audioService = new AudioService(this.notificationService, this.settingsService, this.context.state);
    this.monitorService = new MonitorService(
      maidr.live === true,
      this.audioService,
      this.textService,
      this.notificationService,
    );

    this.brailleService = new BrailleService(
      this.context,
      this.notificationService,
      this.displayService,
      this.settingsService,
    );
    this.goToExtremaService = new GoToExtremaService(
      this.context,
      this.displayService,
    );
    this.reviewService = new ReviewService(
      this.notificationService,
      this.displayService,
      this.textService,
    );

    this.autoplayService = new AutoplayService(this.context, this.notificationService, this.settingsService);
    this.highContrastService = new HighContrastService(
      this.settingsService,
      this.notificationService,
      this.displayService,
      this.figure,
      this.context,
    );
    this.highlightService = new HighlightService(this.settingsService);
    this.descriptionService = new DescriptionService(this.context, this.displayService);
    this.helpService = new HelpService(this.context, this.displayService);
    this.chatService = new ChatService(
      this.displayService,
      this.textService,
      maidr,
    );

    this.textViewModel = new TextViewModel(
      store,
      this.textService,
      this.notificationService,
      this.autoplayService,
      this.audioService,
    );
    this.brailleViewModel = new BrailleViewModel(store, this.brailleService);
    this.goToExtremaViewModel = new GoToExtremaViewModel(
      store,
      this.goToExtremaService,
      this.context,
      this.audioService,
      this.formatterService,
    );
    this.reviewViewModel = new ReviewViewModel(store, this.reviewService);
    this.descriptionViewModel = new DescriptionViewModel(store, this.descriptionService);
    this.displayViewModel = new DisplayViewModel(store, this.displayService);
    this.helpViewModel = new HelpViewModel(store, this.helpService);
    this.settingsViewModel = new SettingsViewModel(store, this.settingsService);

    this.rotorNavigationService = new RotorNavigationService(
      this.context,
      this.textService,
      this.notificationService,
    );
    this.rotorNavigationViewModel = new RotorNavigationViewModel(
      store,
      this.rotorNavigationService,
    );
    this.candlestickDeltaService = new CandlestickDeltaService(
      this.context,
      this.notificationService,
      this.displayService,
      this.rotorNavigationService,
    );
    // Runtime-created virtual traces are not covered by registerObservers(),
    // so the delta service wires the same observer set itself on creation.
    this.candlestickDeltaService.setObserverWirer((trace) => {
      trace.addObserver(this.audioService);
      trace.addObserver(this.brailleService);
      trace.addObserver(this.textService);
      trace.addObserver(this.reviewService);
      trace.addObserver(this.highlightService);
    });
    this.candlestickDeltaViewModel = new CandlestickDeltaViewModel(
      store,
      this.candlestickDeltaService,
      this.notificationService,
    );
    this.chatViewModel = new ChatViewModel(
      store,
      this.chatService,
      this.audioService,
    );

    const commandPaletteService = new CommandPaletteService(
      this.context,
      this.displayService,
    );
    this.commandPaletteViewModel = new CommandPaletteViewModel(
      store,
      commandPaletteService,
    );

    this.keybinding = new KeybindingService({
      context: this.context,

      audioService: this.audioService,
      autoplayService: this.autoplayService,
      brailleService: this.brailleService,
      candlestickDeltaService: this.candlestickDeltaService,
      displayService: this.displayService,
      highContrastService: this.highContrastService,
      highlightService: this.highlightService,
      monitorService: this.monitorService,
      notificationService: this.notificationService,
      rotorNavigationService: this.rotorNavigationService,
      settingsService: this.settingsService,
      textService: this.textService,

      brailleViewModel: this.brailleViewModel,
      candlestickDeltaViewModel: this.candlestickDeltaViewModel,
      chatViewModel: this.chatViewModel,
      commandPaletteViewModel: this.commandPaletteViewModel,
      descriptionViewModel: this.descriptionViewModel,
      goToExtremaViewModel: this.goToExtremaViewModel,
      helpViewModel: this.helpViewModel,
      reviewViewModel: this.reviewViewModel,
      settingsViewModel: this.settingsViewModel,
      textViewModel: this.textViewModel,
      rotorNavigationViewModel: this.rotorNavigationViewModel,
    });
    this.mousebinding = new Mousebindingservice(
      {
        context: this.context,

        audioService: this.audioService,
        autoplayService: this.autoplayService,
        brailleService: this.brailleService,
        candlestickDeltaService: this.candlestickDeltaService,
        displayService: this.displayService,
        highContrastService: this.highContrastService,
        highlightService: this.highlightService,
        monitorService: this.monitorService,
        notificationService: this.notificationService,
        rotorNavigationService: this.rotorNavigationService,
        settingsService: this.settingsService,
        textService: this.textService,

        brailleViewModel: this.brailleViewModel,
        candlestickDeltaViewModel: this.candlestickDeltaViewModel,
        chatViewModel: this.chatViewModel,
        commandPaletteViewModel: this.commandPaletteViewModel,
        descriptionViewModel: this.descriptionViewModel,
        goToExtremaViewModel: this.goToExtremaViewModel,
        helpViewModel: this.helpViewModel,
        reviewViewModel: this.reviewViewModel,
        settingsViewModel: this.settingsViewModel,
        textViewModel: this.textViewModel,
        rotorNavigationViewModel: this.rotorNavigationViewModel,
      },
      this.settingsService,
      this.displayService,
    );

    this.commandExecutor = new CommandExecutor(
      {
        context: this.context,

        audioService: this.audioService,
        autoplayService: this.autoplayService,
        brailleService: this.brailleService,
        candlestickDeltaService: this.candlestickDeltaService,
        displayService: this.displayService,
        highContrastService: this.highContrastService,
        highlightService: this.highlightService,
        monitorService: this.monitorService,
        notificationService: this.notificationService,
        rotorNavigationService: this.rotorNavigationService,
        settingsService: this.settingsService,
        textService: this.textService,

        brailleViewModel: this.brailleViewModel,
        candlestickDeltaViewModel: this.candlestickDeltaViewModel,
        chatViewModel: this.chatViewModel,
        commandPaletteViewModel: this.commandPaletteViewModel,
        descriptionViewModel: this.descriptionViewModel,
        goToExtremaViewModel: this.goToExtremaViewModel,
        helpViewModel: this.helpViewModel,
        reviewViewModel: this.reviewViewModel,
        settingsViewModel: this.settingsViewModel,
        textViewModel: this.textViewModel,
        rotorNavigationViewModel: this.rotorNavigationViewModel,
      },
    );

    // Inject command execution callback into CommandPaletteViewModel (deferred due to circular dependency)
    this.commandPaletteViewModel.setExecuteCommandCallback(
      commandKey => this.commandExecutor.executeCommand(commandKey),
    );

    this.registerViewModels();
    this.registerObservers();
    if (maidr.onNavigate) {
      this.registerNavigateCallback(maidr.onNavigate);
    }
    this.keybinding.register(this.context.scope);
    this.mousebinding.registerEvents();
  }

  /**
   * Displays the initial instruction in the text view without announcing it to screen readers.
   */
  public showInitialInstructionInText(): void {
    const text = this.displayService.getInstruction(false);
    // Keep initial instruction visual-only; enable announce later on first nav update
    this.textViewModel.setAnnounce(false);
    this.textViewModel.update(text);
  }

  /**
   * Initialize high contrast mode if enabled in settings.
   * Call this after the Controller is fully set up and will persist (not the throwaway init).
   */
  public initializeHighContrast(): void {
    this.highContrastService.initializeHighContrast();
  }

  /**
   * Suspend high contrast mode visually (restore original colors).
   * Call this on blur to return the chart to its original appearance.
   */
  public suspendHighContrast(): void {
    this.highContrastService.suspendHighContrast();
  }

  /**
   * Replaces the chart data in place (live/realtime update).
   *
   * Rebuilds the model layer (Figure/Subplot/Trace) from the new data while
   * preserving the user's navigation position, then rewires all observers.
   * Services, view models, and keybindings are untouched, so the update is
   * cheap enough for streaming scenarios.
   *
   * The swap itself is silent; when monitor mode is enabled and the update
   * appended a point, that point is sonified and announced without moving
   * the user's position.
   *
   * @param maidr - The complete replacement MAIDR config (caller-owned copy)
   * @param appended - Location of the newly appended point, for appendData updates
   */
  public updateData(maidr: Maidr, appended?: AppendedPointInfo): void {
    // The virtual delta layer is derived from the current model; a data
    // update rebuilds the figure underneath it, so close it first to keep
    // the navigation stack and keyboard scope consistent.
    if (this.candlestickDeltaService.isActive) {
      this.candlestickDeltaService.deactivate({ silent: true });
      this.notificationService.notify('Reference comparison closed by a data update.');
    }

    // Ordering is load-bearing: the sliding-window shift must be resolved
    // against the OLD figure (the user's current position) before the swap,
    // while announceAppendedPoint below runs against the NEW figure.
    const activeColShift = this.resolveActiveColShift(appended);

    this.figure = this.context.replaceFigure(() => {
      const figure = new Figure(maidr);
      figure.applyLayout(resolveSubplotLayout(figure.subplots));
      return figure;
    }, { activeColShift });

    this.highContrastService.setFigure(this.figure);
    this.formatterService.refresh(maidr);
    this.chatService.updateData(maidr);
    this.monitorService.setLive(maidr.live === true);
    this.registerObservers();
    if (maidr.onNavigate) {
      this.registerNavigateCallback(maidr.onNavigate);
    }

    if (appended) {
      this.announceAppendedPoint(appended);
    }
  }

  /**
   * Computes how far the active trace's cursor must shift left so it stays
   * on the same data point after a sliding-window trim. Zero when nothing
   * was trimmed or the user is positioned on a different trace/group.
   *
   * @param appended - Append info for the incoming update, if any
   * @returns The column shift to apply during position restoration
   */
  private resolveActiveColShift(appended?: AppendedPointInfo): number {
    if (!appended || appended.trimmed === 0 || appended.trimShift !== 'col') {
      return 0;
    }
    try {
      // The cursor follows trimmed data only on the focused layer/series —
      // the same focus rule that scopes monitor announcements. Evaluating the
      // predicate against the OLD figure is safe even for appends that start
      // a new series: the window trims per group, so a brand-new group (one
      // point) always has trimmed === 0 and returns above.
      return isAppendedPointFocused(this.figure, appended) ? appended.trimmed : 0;
    } catch (error) {
      console.warn('[maidr] Failed to resolve sliding-window shift:', error);
      return 0;
    }
  }

  /**
   * Sonifies and announces a newly appended data point through the monitor
   * service, computing its state without moving the user's cursor.
   *
   * Only appends to the layer the user is currently focused on are
   * announced: multi-layer charts stream one point per layer per tick, and
   * sonifying every layer would bury the focused signal in overlapping
   * tones. Switching layers (PageUp/PageDown) switches what is monitored.
   *
   * @param appended - Location of the appended point in the new figure
   */
  private announceAppendedPoint(appended: AppendedPointInfo): void {
    if (!this.monitorService.isEnabled) {
      return;
    }
    try {
      if (!isAppendedPointFocused(this.figure, appended)) {
        return;
      }
      // The focused layer's trace IS the active trace (one single-trace row
      // per layer; see Subplot.activeLayerIndex) — the focus predicate above
      // guarantees the indices are in range, so no null guard is needed;
      // any residual access failure lands in the catch below. Compute the
      // new point's state without moving the user's cursor; observers are
      // only notified via MonitorService.
      const state = this.figure.activeSubplot.activeTrace.getStateAt(appended.row, appended.col);
      this.monitorService.handleNewPoint(state);
    } catch (error) {
      console.warn('[maidr] Failed to announce appended data point:', error);
    }
  }

  /**
   * Cleans up all services, view models, and event listeners.
   */
  public dispose(): void {
    this.keybinding.unregister();
    this.mousebinding.dispose();
    this.commandExecutor.dispose();

    this.viewModelRegistry.dispose();
    this.settingsViewModel.dispose();
    this.chatViewModel.dispose();
    this.helpViewModel.dispose();
    this.descriptionViewModel.dispose();
    this.displayViewModel.dispose();
    this.goToExtremaViewModel.dispose();
    this.reviewViewModel.dispose();
    this.candlestickDeltaViewModel.dispose();
    this.brailleViewModel.dispose();
    this.textViewModel.dispose();
    this.commandPaletteViewModel.dispose();
    this.rotorNavigationViewModel.dispose();

    this.highContrastService.dispose();
    this.highlightService.dispose();
    this.autoplayService.dispose();
    this.monitorService.dispose();

    this.chatService.dispose();
    this.candlestickDeltaService.dispose();
    this.textService.dispose();
    this.reviewService.dispose();
    this.brailleService.dispose();
    this.audioService.dispose();
    this.formatterService.dispose();

    this.settingsService.dispose();
    this.notificationService.dispose();
    this.displayService.dispose();
    this.context.dispose();
    this.figure.dispose();
  }

  /**
   * Returns the context value for React dependency injection.
   * Used by the React component tree to access view models and command executor.
   */
  public getContextValue(): { viewModelRegistry: ViewModelRegistry; commandExecutor: CommandExecutor } {
    return {
      viewModelRegistry: this.viewModelRegistry,
      commandExecutor: this.commandExecutor,
    };
  }

  /**
   * Registers all view models with this controller's registry.
   */
  private registerViewModels(): void {
    this.viewModelRegistry.register('text', this.textViewModel);
    this.viewModelRegistry.register('braille', this.brailleViewModel);
    this.viewModelRegistry.register('candlestickDelta', this.candlestickDeltaViewModel);
    this.viewModelRegistry.register('goToExtrema', this.goToExtremaViewModel);
    this.viewModelRegistry.register('review', this.reviewViewModel);
    this.viewModelRegistry.register('description', this.descriptionViewModel);
    this.viewModelRegistry.register('display', this.displayViewModel);
    this.viewModelRegistry.register('help', this.helpViewModel);
    this.viewModelRegistry.register('chat', this.chatViewModel);
    this.viewModelRegistry.register('settings', this.settingsViewModel);
    this.viewModelRegistry.register('commandPalette', this.commandPaletteViewModel);
    this.viewModelRegistry.register('commandExecutor', this.commandExecutor);
    this.viewModelRegistry.register('rotor', this.rotorNavigationViewModel);
  }

  /**
   * Registers observers to the figure, subplots, and traces for state updates.
   */
  private registerObservers(): void {
    this.figure.addObserver(this.textService);
    this.figure.addObserver(this.audioService);
    this.figure.addObserver(this.highlightService);
    this.figure.addObserver(this.reviewService);
    this.figure.subplots.forEach(subplotRow => subplotRow.forEach((subplot) => {
      subplot.addObserver(this.textService);
      subplot.addObserver(this.audioService);
      subplot.addObserver(this.brailleService);
      subplot.addObserver(this.highlightService);
      subplot.addObserver(this.reviewService);
      subplot.traces.forEach(traceRow => traceRow.forEach((trace) => {
        trace.addObserver(this.audioService);
        trace.addObserver(this.brailleService);
        trace.addObserver(this.textService);
        trace.addObserver(this.reviewService);
        trace.addObserver(this.highlightService);
      }));
    }));
  }

  /**
   * Registers a navigate callback observer on all traces.
   * Used by canvas-based charting libraries (e.g., Chart.js) for visual highlighting.
   */
  private registerNavigateCallback(callback: NavigateCallback): void {
    const observer: Observer<TraceState> = {
      update: (state: TraceState) => {
        if (!state.empty && !state.braille.empty) {
          callback({
            layerId: state.layerId,
            row: state.braille.row,
            col: state.braille.col,
          });
        }
      },
    };
    this.figure.subplots.forEach(subplotRow => subplotRow.forEach((subplot) => {
      subplot.traces.forEach(traceRow => traceRow.forEach((trace) => {
        trace.addObserver(observer);
      }));
    }));
  }
}
